import React, { useEffect, useState } from 'react';

interface DisplayNamePromptProps {
  isOpen: boolean;
  onSave: (name: string) => Promise<void>;
}

const MAX_NAME_LENGTH = 40;

const DisplayNamePrompt: React.FC<DisplayNamePromptProps> = ({ isOpen, onSave }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();

    if (!trimmed) {
      setError('Display name is required.');
      return;
    }

    if (trimmed.length > MAX_NAME_LENGTH) {
      setError('Display name must be 40 characters or fewer.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSave(trimmed);
    } catch (err) {
      setError('We could not save your name. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="display-name-title">
        <div className="modal__header">
          <h2 id="display-name-title">Set your display name</h2>
        </div>
        <div className="modal__content">
          <p className="modal__helper">Choose a name your teammates will recognize.</p>
          <form onSubmit={handleSubmit} className="modal__form">
            <label htmlFor="display-name" className="modal__label">
              Display name
            </label>
            <input
              id="display-name"
              type="text"
              className="modal__input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={MAX_NAME_LENGTH}
              autoComplete="name"
              autoFocus
              required
              disabled={isSubmitting}
            />
            <button type="submit" className="modal__button" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save name'}
            </button>
            {error ? (
              <p className="modal__status modal__status--error" role="alert">
                {error}
              </p>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
};

export default DisplayNamePrompt;
